import { useCallback, useEffect, useMemo, useState } from 'react'
import { orderBy, range, sortBy } from 'lodash'
import { DateTime } from 'luxon'
import currency from 'currency.js'

import {
  cancelContractorPayment,
  fetchContractorPayments,
  fetchContractors,
} from '../payrollActions'
import { selectContractorByUuid } from '../payroll.selectors'
import { getContractorName } from '../helpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { formatCurrency } from '@/utils/currencyHelpers'
import { Text, Button, Table, Link, Select, Loader, Dialog } from '@/ui'
import { DATE_FORMATS, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { Gusto_ContractorPayment } from '../generated_gusto_types'
import { TextColors, TypographyStyles } from '@/styles'

// TODO: Migrate to V2
import { valsToDropdownOptions } from '@/base'

const CancelModal = ({
  payment,
  close,
  fetchPayments,
  open,
}: {
  payment: Gusto_ContractorPayment
  close: () => void
  fetchPayments: () => void
  open: boolean
}) => {
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(false)
  const contractor = useReselector(
    selectContractorByUuid,
    payment.contractor_uuid
  )

  const cancelPayroll = useCallback(async () => {
    setLoading(true)
    // This is a very slow call (4 sec)
    const res = await cancelContractorPayment(payment.uuid)(dispatch)

    if (res) {
      // refetch payment (will remove cancelled payment)
      fetchPayments()
      close()
    }
    setLoading(false)
  }, [close, dispatch, payment.uuid, fetchPayments])

  return (
    <Dialog open={open} onOpenChange={(open) => !open && close()}>
      <Dialog.Trigger>
        <Button variant="link">Cancel Payroll</Button>
      </Dialog.Trigger>

      <Dialog.Content className="max-w-sm">
        <Dialog.Title>Cancel Contractor Payment</Dialog.Title>

        <div className="flex flex-col gap-4 mt-2">
          <Text>
            <b>
              Warning you are about to cancel {getContractorName(contractor)}
              &apos;s{' '}
              {DateTime.fromFormat(
                payment.date,
                DATE_FORMATS_LUXON.GUSTO_SUBMIT
              ).toFormat(DATE_FORMATS.DISPLAY_SHORT)}{' '}
              payment
            </b>
          </Text>

          <Text>
            If you continue, the payment will not be sent to the contractor and
            the payment record will be removed.
          </Text>

          {!loading && (
            <Dialog.Footer>
              <Button
                onClick={cancelPayroll}
                className="w-full bg-red-600 hover:bg-red-700 text-white"
              >
                Cancel Payment
              </Button>
              <Button variant="secondary" onClick={close} className="w-full">
                Go Back
              </Button>
            </Dialog.Footer>
          )}

          {loading && (
            <div className="w-full flex justify-center py-4">
              <Loader />
            </div>
          )}
        </div>
      </Dialog.Content>
    </Dialog>
  )
}

const PaymentRow = ({
  payment,
  fetchPayments,
}: {
  payment: Gusto_ContractorPayment
  fetchPayments: () => void
}) => {
  const [showModal, setShowModal] = useState(false)

  const contractor = useReselector(
    selectContractorByUuid,
    payment.contractor_uuid
  )

  const paymentDate = DateTime.fromFormat(
    payment.date,
    DATE_FORMATS_LUXON.GUSTO_SUBMIT
  )

  return (
    <Table.Row key={payment.uuid}>
      <Table.Cell>
        <Text>{paymentDate.toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)}</Text>
        {paymentDate > DateTime.now() && (
          <Button onClick={() => setShowModal(true)} variant="link">
            Cancel Payroll
          </Button>
        )}
      </Table.Cell>
      <Table.Cell>{getContractorName(contractor)}</Table.Cell>
      <Table.Cell>
        {formatCurrency(
          // Wage total include bonus
          currency(payment.wage_total).add(payment.reimbursement)
        )}
      </Table.Cell>
      <Table.Cell>
        <Link href={`/payroll/contractor_history/${payment.uuid}`}>
          View Details
        </Link>
      </Table.Cell>
      {showModal && (
        <CancelModal
          open={showModal}
          payment={payment}
          close={() => setShowModal(false)}
          fetchPayments={fetchPayments}
        />
      )}
    </Table.Row>
  )
}

export const ContractorPayrollHistoryTab = () => {
  const dispatch = useAppDispatch()

  const [payments, setPayments] = useState<Gusto_ContractorPayment[]>([])
  const [year, setYear] = useState(DateTime.now().year)

  const yearOptions = valsToDropdownOptions(
    // A couple of weeks are added on to handle case where payment date is next year at end of year
    range(2020, DateTime.now().plus({ weeks: 2 }).year + 1).reverse()
  )

  const paymentsToShow = useMemo(
    () =>
      orderBy(
        Object.values(payments).filter(
          (payment) =>
            DateTime.fromFormat(payment.date, DATE_FORMATS_LUXON.GUSTO_SUBMIT)
              .year === year
        ),
        (payment) => payment.date,
        ['desc']
      ),
    [payments, year]
  )

  const fetchPayments = useCallback(async () => {
    // Add start and end date here to get back all of the contractor payments
    const res = await fetchContractorPayments({
      start_date: '2020-01-01',
      end_date: DateTime.now()
        .plus({ month: 1 })
        .toFormat(DATE_FORMATS.GUSTO_SUBMIT),
    })(dispatch)

    if (res) {
      const payments = res.contractor_payments.reduce<
        Gusto_ContractorPayment[]
      >((prev, payment) => {
        // Contractor uuid doesn't come back with payload so add it here
        return [
          ...prev,
          ...payment.payments.map((val) => ({
            ...val,
            contractor_uuid: payment.contractor_uuid,
          })),
        ]
      }, [])

      setPayments(sortBy(payments, 'date'))
    }
  }, [dispatch])

  useEffect(() => {
    fetchPayments()
    dispatch(fetchContractors())
  }, [dispatch, fetchPayments])

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <Text variant={TypographyStyles.H3} className="w-1/2">
          <b>Contractor Payments</b>
        </Text>
        <div className="w-1/2 flex justify-end">
          <Select options={yearOptions} value={year} onValueChange={setYear} />
        </div>
      </div>

      <Text variant={TypographyStyles.BODY_MD}>
        Below is a list of payments you&apos;ve made to your contractors
      </Text>

      {/* Optional empty spacer row can be a div with height if needed */}
      <div className="h-2" />

      <Table>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Pay Date</Table.HeaderCell>
            <Table.HeaderCell>Contractor Paid</Table.HeaderCell>
            <Table.HeaderCell>Total Amount</Table.HeaderCell>
            <Table.HeaderCell />
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {paymentsToShow.length === 0 && (
            <Table.Row>
              <Table.Cell colSpan={4}>
                <Text
                  variant={TypographyStyles.BODY_SM}
                  className="text-center"
                  color={TextColors.MUTED}
                >
                  No payments found
                </Text>
              </Table.Cell>
            </Table.Row>
          )}
          {paymentsToShow.length > 0 &&
            paymentsToShow.map((payment) => (
              <PaymentRow
                key={payment.uuid}
                payment={payment}
                fetchPayments={fetchPayments}
              />
            ))}
        </Table.Body>
      </Table>
    </div>
  )
}
